/**
 * Convierte todas las tablas de datos en tablas ordenables sin acoplarse a
 * una vista concreta. Las columnas de selección y acciones se omiten porque
 * no representan datos comparables.
 */
function inicializarTablasOrdenables() {
    const collator = new Intl.Collator('es', {
        numeric: true,
        sensitivity: 'base',
    });
    const etiquetasNumericas = new Set([
        'cantidad',
        'reservado',
        'reserva',
        'disponible',
        'solicitud',
        'autorizado',
        'cajas',
        'kilos',
        'unidades',
    ]);
    const normalizarTexto = (valor) => valor
        .replace(/\s+/g, ' ')
        .trim();
    const normalizarEtiqueta = (valor) => normalizarTexto(valor)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('es');

    function convertirNumero(valor) {
        const original = normalizarTexto(valor).replace(/\u00a0/g, ' ');
        if (!original || original === '-') return Number.NaN;

        // Evita interpretar códigos alfanuméricos como cantidades.
        if (!/^[\s#$€£%+\-\d.,]+$/.test(original)) return Number.NaN;

        let numero = original.replace(/[^\d.,+\-]/g, '');
        const puntos = (numero.match(/\./g) || []).length;
        const comas = (numero.match(/,/g) || []).length;

        if (puntos && comas) {
            const separadorDecimal = numero.lastIndexOf('.') > numero.lastIndexOf(',') ? '.' : ',';
            const separadorMiles = separadorDecimal === '.' ? /,/g : /\./g;
            numero = numero.replace(separadorMiles, '').replace(separadorDecimal, '.');
        } else if (puntos || comas) {
            const separador = puntos ? '.' : ',';
            const partes = numero.split(separador);
            const esMiles = partes.length > 2 || (partes.length === 2 && partes[1].length === 3);
            numero = esMiles ? partes.join('') : numero.replace(separador, '.');
        }

        const resultado = Number(numero);
        return Number.isFinite(resultado) ? resultado : Number.NaN;
    }

    function convertirFecha(valor) {
        const texto = normalizarTexto(valor);
        if (!texto || texto === '-') return Number.NaN;

        let coincidencia = texto.match(
            /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
        );
        if (coincidencia) {
            return Date.UTC(
                Number(coincidencia[1]),
                Number(coincidencia[2]) - 1,
                Number(coincidencia[3]),
                Number(coincidencia[4] || 0),
                Number(coincidencia[5] || 0),
                Number(coincidencia[6] || 0)
            );
        }

        coincidencia = texto.match(
            /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
        );
        if (coincidencia) {
            return Date.UTC(
                Number(coincidencia[3]),
                Number(coincidencia[2]) - 1,
                Number(coincidencia[1]),
                Number(coincidencia[4] || 0),
                Number(coincidencia[5] || 0),
                Number(coincidencia[6] || 0)
            );
        }

        return Number.NaN;
    }

    function obtenerFilasOrdenables(tabla, indiceColumna) {
        return Array.from(tabla.tBodies).flatMap((tbody) =>
            Array.from(tbody.rows)
                .filter((fila) =>
                    !fila.classList.contains('no-data') &&
                    !fila.classList.contains('fila-esqueleto') &&
                    fila.cells.length > indiceColumna &&
                    !fila.cells[0].hasAttribute('colspan')
                )
                .map((fila) => ({ fila, tbody }))
        );
    }

    function detectarTipo(encabezado, filas, indiceColumna) {
        const tipoDeclarado = encabezado.dataset.sortType;
        if (tipoDeclarado) return tipoDeclarado;

        const etiqueta = normalizarEtiqueta(encabezado.textContent);
        if (etiqueta === 'etd' || etiqueta === 'eta' || etiqueta.includes('fecha')) {
            return 'date';
        }
        if (etiquetasNumericas.has(etiqueta)) return 'number';
        // Los identificadores se comparan como texto con orden natural. Esto
        // conserva ceros iniciales y evita perder precisión en códigos largos.
        if (etiqueta.includes('codigo') || etiqueta.startsWith('cod.')) {
            return 'text';
        }

        const valores = filas
            .map(({ fila }) => normalizarTexto(fila.cells[indiceColumna].textContent))
            .filter((valor) => valor && valor !== '-');

        if (valores.length && valores.every((valor) => !Number.isNaN(convertirFecha(valor)))) {
            return 'date';
        }
        if (valores.length && valores.every((valor) => !Number.isNaN(convertirNumero(valor)))) {
            return 'number';
        }
        return 'text';
    }

    function obtenerValor(celda, tipo) {
        const valor = normalizarTexto(celda.textContent);
        if (tipo === 'number') return convertirNumero(valor);
        if (tipo === 'date') return convertirFecha(valor);
        return valor;
    }

    function actualizarAccesibilidad(tabla) {
        const encabezados = Array.from(tabla.querySelectorAll('thead th[data-sortable="true"]'));
        encabezados.forEach((encabezado, indice) => {
            const activo = indice === tabla._sortColumn;
            const direccion = activo ? tabla._sortDirection : null;
            const boton = encabezado.querySelector('.tabla-sort-btn');
            const etiqueta = boton ? boton.querySelector('.tabla-sort-label').textContent : '';

            encabezado.setAttribute('aria-sort', direccion || 'none');
            encabezado.dataset.sortDirection = direccion || 'none';
            if (boton) {
                let siguienteAccion = 'orden ascendente';
                if (!activo && encabezado.dataset.sortTypeResolved === 'date') {
                    siguienteAccion = 'más reciente a más antigua';
                } else if (activo && direccion === 'ascending') {
                    siguienteAccion = 'orden descendente';
                }
                boton.setAttribute('aria-label', `Ordenar ${etiqueta}, ${siguienteAccion}`);
                boton.title = boton.getAttribute('aria-label');
            }
        });
    }

    function ordenarTabla(tabla, indiceColumna, direccion, mantenerDireccion = false) {
        const encabezados = Array.from(tabla.querySelectorAll('thead th[data-sortable="true"]'));
        const encabezado = encabezados[indiceColumna];
        if (!encabezado) return;

        const indiceReal = Number(encabezado.dataset.sortColumn);
        const filas = obtenerFilasOrdenables(tabla, indiceReal);
        const tipo = detectarTipo(encabezado, filas, indiceReal);
        encabezado.dataset.sortTypeResolved = tipo;

        if (!mantenerDireccion) {
            if (tabla._sortColumn === indiceColumna) {
                direccion = tabla._sortDirection === 'ascending' ? 'descending' : 'ascending';
            } else {
                direccion = tipo === 'date' ? 'descending' : 'ascending';
            }
        }

        const multiplicador = direccion === 'ascending' ? 1 : -1;
        const ordenadas = filas
            .map((entrada, posicionOriginal) => ({
                ...entrada,
                posicionOriginal,
                valor: obtenerValor(entrada.fila.cells[indiceReal], tipo),
            }))
            .sort((a, b) => {
                const vacioA = a.valor === '' || Number.isNaN(a.valor);
                const vacioB = b.valor === '' || Number.isNaN(b.valor);
                if (vacioA !== vacioB) return vacioA ? 1 : -1;
                if (vacioA && vacioB) return a.posicionOriginal - b.posicionOriginal;

                const comparacion = tipo === 'text'
                    ? collator.compare(a.valor, b.valor)
                    : a.valor - b.valor;
                return comparacion === 0
                    ? a.posicionOriginal - b.posicionOriginal
                    : comparacion * multiplicador;
            });

        // Solo toca el DOM si el orden realmente cambió; así el observador no
        // genera ciclos y se conservan los listeners de cada fila.
        const requiereCambio = ordenadas.some(({ fila }, indice) => fila !== filas[indice].fila);
        if (requiereCambio) {
            ordenadas.forEach(({ fila, tbody }) => tbody.appendChild(fila));
        }

        tabla._sortColumn = indiceColumna;
        tabla._sortDirection = direccion;
        actualizarAccesibilidad(tabla);
    }

    document.querySelectorAll('table.tabla').forEach((tabla) => {
        const encabezados = Array.from(tabla.querySelectorAll('thead th'));
        let indiceOrdenable = 0;

        encabezados.forEach((encabezado, indiceReal) => {
            encabezado.setAttribute('scope', 'col');
            const etiqueta = normalizarTexto(encabezado.textContent);
            const noOrdenable = !etiqueta ||
                normalizarEtiqueta(etiqueta) === 'acciones' ||
                encabezado.querySelector('input, button, select, a');

            if (noOrdenable) {
                encabezado.dataset.sortable = 'false';
                return;
            }

            const boton = document.createElement('button');
            boton.type = 'button';
            boton.className = 'tabla-sort-btn';
            boton.innerHTML = '<span class="tabla-sort-label"></span><span class="tabla-sort-icon" aria-hidden="true"></span>';
            boton.querySelector('.tabla-sort-label').textContent = etiqueta;
            encabezado.textContent = '';
            encabezado.appendChild(boton);
            encabezado.dataset.sortable = 'true';
            encabezado.dataset.sortColumn = String(indiceReal);
            encabezado.setAttribute('aria-sort', 'none');

            const columnaOrdenable = indiceOrdenable++;
            boton.addEventListener('click', () => {
                ordenarTabla(tabla, columnaOrdenable);
            });
        });

        actualizarAccesibilidad(tabla);

        let actualizacionPendiente = false;
        const observador = new MutationObserver(() => {
            if (tabla._sortColumn === undefined || actualizacionPendiente) return;
            actualizacionPendiente = true;
            queueMicrotask(() => {
                actualizacionPendiente = false;
                ordenarTabla(tabla, tabla._sortColumn, tabla._sortDirection, true);
            });
        });
        Array.from(tabla.tBodies).forEach((tbody) => {
            observador.observe(tbody, { childList: true });
        });
    });
}

document.addEventListener('DOMContentLoaded', () => {
    inicializarTablasOrdenables();
    
    //MENU HAMBURGEUSA
    const menuHamburguesa = document.querySelector('.cont-menu-hamburguesa');
    const containerMenu = document.querySelector('.container-menu');

    if(menuHamburguesa){
        menuHamburguesa.addEventListener('click', () => {
            const abierto = containerMenu.classList.toggle('mostrar');
            menuHamburguesa.setAttribute('aria-expanded', abierto ? 'true' : 'false');
            menuHamburguesa.setAttribute('aria-label', abierto ? 'Cerrar menú' : 'Abrir menú');
        });
    }

    /////////

    //INPUT FORMATO NUMERO CON PUNTO
    const inputsNumericos = document.querySelectorAll('input[id="cantidad"]');
    inputsNumericos.forEach(input => {
        input.addEventListener('input', function() {
            let valorInput = this.value.replace(/\D/g, '');
            const numberFormat = new Intl.NumberFormat('en-US'); // 'es-ES' para español en España
            const valorFormateado = numberFormat.format(valorInput);
            input.value = valorFormateado;
        });
    });
    /////////

    //INPUT FORMATO NUMERO SIN PUNTO
    const inputsNumerico = document.querySelectorAll('input[id="numero"]');
    inputsNumerico.forEach(input => {
        input.addEventListener('input', function() {
            let valorInput = this.value.replace(/\D/g, '');
            input.value = valorInput;
        });
        input.addEventListener('focus', function() {
            this.value = '';
        });
    });
    /////////
    
    //QUITAR PLACEHOLDER DE INPUTS TIPO DATE
    // Seleccionar todos los inputs de tipo date
    const dateInputs = document.querySelectorAll('input[type="date"]');

    // Iterar sobre cada input y agregar eventos
    dateInputs.forEach(input => {
        // Añadir un texto cuando el campo esté vacío
        if (!input.value) {
            input.type = 'text';
            input.classList.add('placeholder');
            input.setAttribute('data-placeholder', 'Selecciona una fecha');
            input.value = '';
        }

        // Al hacer foco, eliminar el "placeholder"
        input.addEventListener('focus', function () {
            if (input.classList.contains('placeholder')) {
                input.type = 'date';
                input.classList.remove('placeholder');
                input.value = '';
            }
        });

        // Al perder foco, restaurar el "placeholder" si está vacío
        input.addEventListener('blur', function () {
            if (!input.value) {
                input.type = 'text';
                input.classList.add('placeholder');
                input.setAttribute('data-placeholder', 'Selecciona una fecha');
            }
        });
    });
    // Definir las clases de estado
        const estadoClases = {
        1: 'selectLista-amarillo',
        2: 'selectLista-verde',
        3: 'selectLista-rojo',
        4: 'selectLista-rojo'
    };
    //Función para actualizar las clases del estado
    function actualizarClaseEstado(estado, estadoClass) {
        //Limpiar las clases existentes
        Object.values(estadoClases).forEach(clase => {
            estadoClass.classList.remove(clase);
        });
        //Añadir la clase correspondiente según el valor del estado
        if (estadoClases[estado]) {
            estadoClass.classList.add(estadoClases[estado]);
        }
    }
    if(document.querySelector('.solicitud-reserva #id_estado')){
        //Listener para el cambio de estado en el formulario modal
            document.querySelector('.solicitud-reserva #id_estado').addEventListener('change', function() {
            actualizarClaseEstado(this.value, this);
            if(this.value == 3 || this.value == 1){
                document.querySelector('.editarReserva .solicitud-reserva #cantidad').value = 0;
            }
        });
    }
    
    // El cierre con ESC (y el desbloqueo de scroll del body) ya lo gestiona
    // VentanaModal.js de forma centralizada.

});
